import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, NavLink, Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Phone, MessageCircle, Mail, ShieldCheck, Swords, Clock3, BadgeDollarSign, CalendarDays, Star, Menu, X } from "lucide-react";
import Admin from "@/Admin";
import { PayPage, PaymentSuccess, PaymentCancel } from "@/Pay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE_DISPLAY = "470-692-2559";
const PHONE_TEL = "tel:4706922559";
const PHONE_SMS = "sms:4706922559";
const WHATSAPP = "https://wa.me/14706922559";
const EMAIL = "quote@epicexpectation.info";
const EMAIL_GMAIL = "Tristanturmon@gmail.com";

const SERVICES = [
  {
    value: "carpet",
    name: "Carpet Cleaning",
    tag: "Deep Extraction & Stain Warfare",
    price: "From $45/room · 3-Room Special $89",
    desc: "High-powered hot water extraction that removes embedded dirt, allergens, pet odors, and stubborn stains. Restores carpets to battle-ready condition.",
    includes: ["Pre-treatment of high-traffic areas and stains", "Hot water extraction", "Deodorizing", "Fabric protection options", "Fast-dry technology"],
    img: "/assets/service-carpet-cleaning.webp",
  },
  {
    value: "upholstery",
    name: "Upholstery & Rug Cleaning",
    tag: "Precision Care for Soft Assets",
    price: "Sofa + Loveseat Combo $95",
    desc: "Fabric-specific cleaning that is gentle on delicate materials yet powerful against dirt, oils, and odors. Perfect for furniture and area rugs.",
    includes: ["Fiber identification", "Customized solutions", "Spot treatment + full extraction", "Pet odor neutralization", "Optional stain guard"],
    img: "/assets/service-upholstery-rug-cleaning.webp",
  },
  {
    value: "detailing",
    name: "Mobile Car Detailing",
    tag: "Elite Automotive Detailing, Delivered",
    price: "Full Mobile Detail from $135",
    desc: "Professional interior and exterior detailing brought directly to your driveway. No drop-off required.",
    includes: ["Exterior hand wash, clay bar, polish, wax/sealant", "Interior vacuum, shampoo, leather conditioning", "Odor elimination", "Wheel & tire detailing", "Optional ceramic coating or paint correction"],
    img: "/assets/service-mobile-car-detailing.webp",
  },
  {
    value: "landscaping",
    name: "Landscaping & Lawn Care",
    tag: "Precision Grounds Maintenance",
    price: "From $55 per visit",
    desc: "Disciplined lawn care and landscaping that makes properties look sharp and commanding.",
    includes: ["Precision mowing, edging, trimming", "Fertilization and weed control", "Mulching, bed maintenance, seasonal cleanups", "Landscape design and installation available"],
    img: "/assets/service-landscaping-lawn-care.webp",
  },
  {
    value: "maid",
    name: "Maid & House Cleaning",
    tag: "Full-Spectrum Interior Operations",
    price: "$120 flat · Deep clean $18/hr",
    desc: "Systematic, thorough cleaning sweeps that leave every surface spotless. Ideal for recurring maintenance or deep resets.",
    includes: ["Kitchen & bathroom deep clean", "Dusting, vacuuming, mopping", "Baseboards, fixtures, high-touch surfaces", "Recurring schedules: weekly, bi-weekly, monthly"],
    img: "/assets/service-maid-house-cleaning.webp",
  },
  {
    value: "pressure",
    name: "Pressure Washing",
    tag: "High-Powered Surface Domination",
    price: "$250 house · $399 house + driveway",
    desc: "Blasts driveways, sidewalks, siding, decks, fences, and patios back to pristine condition. Instant visual impact.",
    includes: ["Driveways, walkways, concrete", "House siding and brick", "Decks, patios, fences", "Soft-wash options for delicate surfaces"],
    img: "/assets/service-pressure-washing.webp",
  },
  {
    value: "windows",
    name: "Window & Gutter Cleaning",
    tag: "Clear Lines of Sight + Debris Elimination",
    price: "Gutters $125 · $8–$15 per window",
    desc: "Streak-free glass and fully cleared gutters for better curb appeal and proper water flow.",
    includes: ["Interior + exterior window cleaning", "Screen cleaning", "Full gutter clearing and flushing", "Downspout inspection"],
    img: "/assets/service-window-gutter-cleaning.webp",
  },
];

const WHY_US = [
  { icon: Swords, title: "One Call Covers It All", text: "Seven services, one crew. No juggling contractors — the Epic Squad handles home, lawn and auto." },
  { icon: ShieldCheck, title: "Owner-Run & Accountable", text: "Tristian personally stands behind every job. You deal with the man in charge, not a call center." },
  { icon: Clock3, title: "Fast, Free Quotes", text: "Flexible scheduling and quick turnaround on every quote request — call, text or book online." },
  { icon: BadgeDollarSign, title: "Fair Prices, Sharp Detail", text: "Honest pricing with military-grade attention to detail on every task we take on." },
];

const REVIEWS = [
  { name: "Sarah L.", service: "Carpet Cleaning", text: "Absolutely saved our living room carpet! Stubborn stains entirely gone.", img: "/assets/review-carpet.webp" },
  { name: "David K.", service: "Upholstery & Rug Cleaning", text: "Our sectional sofa looks brand new! Incredible attention to detail.", img: "/assets/review-upholstery.webp" },
  { name: "Mike R.", service: "Mobile Car Detailing", text: "Convenient, professional, and my car has never looked this flawless!", img: "/assets/review-detailing.webp" },
  { name: "Marcus T.", service: "Landscaping & Lawn Care", text: "Completely revived our overgrown yard. Highly recommend Tristian and the squad!", img: "/assets/review-landscaping.webp" },
  { name: "Jessica P.", service: "Maid & House Cleaning", text: "Outstanding work! Thorough, trustworthy, and the house smells amazing.", img: "/assets/review-maid.webp" },
  { name: "Robert B.", service: "Pressure Washing", text: "Instantly boosted our curb appeal! Years of grime washed away in hours.", img: "/assets/review-pressure.webp" },
  { name: "Emily S.", service: "Window & Gutter Cleaning", text: "Gutters are spotless and the windows are so clean they look invisible!", img: "/assets/review-windows.webp" },
];

const SQUAD_PHOTOS = [
  { src: "/assets/owner.webp", caption: "Commanding Officer Tristian Turmon" },
  { src: "/assets/service-carpet-cleaning.webp", caption: "Carpet Cleaning Unit" },
  { src: "/assets/service-mobile-car-detailing.webp", caption: "Mobile Detailing Unit" },
  { src: "/assets/service-landscaping-lawn-care.webp", caption: "Grounds Crew" },
  { src: "/assets/service-pressure-washing.webp", caption: "Pressure Washing Unit" },
  { src: "/assets/billboard-victory.webp", caption: "Victory Over Every Task" },
];

const NAV_LINKS = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/services", label: "Services", testid: "nav-services-link" },
  { to: "/reviews", label: "Reviews", testid: "nav-reviews-link" },
  { to: "/squad", label: "The Squad", testid: "nav-squad-link" },
  { to: "/careers", label: "Careers", testid: "nav-careers-link" },
  { to: "/book", label: "Book Online", testid: "nav-book-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header className="site-header" data-testid="site-header">
      <Link to="/" className="logo-wrap" data-testid="logo-button" onClick={() => setMenuOpen(false)}>
        <img src="/assets/logo.webp" alt="Epic Expectations logo" className="logo-img" data-testid="logo-image" />
        <span className="logo neon-text-blue">EPIC EXPECTATIONS&trade;</span>
      </Link>
      <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)} data-testid="menu-toggle-button" aria-label="Toggle menu">
        {menuOpen ? <X size={26} /> : <Menu size={26} />}
      </button>
      <nav className={menuOpen ? "nav-open" : ""} data-testid="main-nav">
        {NAV_LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/"}
            className={({ isActive }) => (isActive ? "nav-link nav-active" : "nav-link")}
            data-testid={l.testid}
            onClick={() => setMenuOpen(false)}
          >
            {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="header-actions">
        <a href={PHONE_TEL} className="btn-call-nav" data-testid="nav-call-button">
          <Phone size={16} /> {PHONE_DISPLAY}
        </a>
        <Link to="/book" className="btn-quote-nav" data-testid="nav-quote-button">Get a Quote</Link>
      </div>
    </header>
  );
};

const Hero = () => {
  const navigate = useNavigate();
  return (
    <section
      className="hero"
      data-testid="hero-section"
      style={{ backgroundImage: `linear-gradient(rgba(13, 13, 13, 0.88), rgba(13, 13, 13, 0.94)), url(${process.env.PUBLIC_URL}/assets/billboard-victory.webp)` }}
    >
      <img src="/assets/logo.webp" alt="Epic Expectations neon spartan logo" className="hero-logo" data-testid="hero-logo" />
      <p className="hero-slogan neon-text-green" data-testid="hero-slogan">Victory Over Every Task&trade;</p>
      <h1 className="neon-text-pink" data-testid="hero-heading">Battle-Tested Care For Home &amp; Lawn&trade;</h1>
      <p className="hero-sub neon-text-blue" data-testid="hero-subheading">
        The Epic Squad delivers elite, multi-service solutions for your property. One Call. Epic Results.&trade;
      </p>
      <div className="hero-buttons">
        <button className="btn btn-primary" data-testid="hero-book-button" onClick={() => navigate("/book")}>Book Now</button>
        <button className="btn btn-secondary" data-testid="hero-quote-button" onClick={() => navigate("/book")}>Request a Free Quote</button>
        <a href={PHONE_TEL} className="btn btn-call" data-testid="hero-call-button">
          <Phone size={18} /> Call {PHONE_DISPLAY}
        </a>
      </div>
    </section>
  );
};

const Services = () => (
  <section id="services" className="services-section" data-testid="services-section">
    <h2 className="neon-text-blue" data-testid="services-heading">OUR 7 ELITE SERVICES</h2>
    <p className="section-tagline neon-text-green" data-testid="services-tagline">The 7 Elite Services for Home &amp; Lawn</p>
    <div className="grid">
      {SERVICES.map((s) => (
        <div className="card" key={s.value} data-testid={`service-card-${s.value}`}>
          <a href={s.img} target="_blank" rel="noopener noreferrer" title="View full image">
            <img src={s.img} alt={`${s.name} — Epic Expectations`} className="card-img" loading="lazy" />
          </a>
          <h3>{s.name}</h3>
          <p className="card-tag">{s.tag}</p>
          <p className="card-price" data-testid={`service-price-${s.value}`}>{s.price}</p>
          <p>{s.desc}</p>
          <ul className="card-includes">
            {s.includes.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </section>
);

const StackAdvantage = () => {
  const navigate = useNavigate();
  return (
    <section className="stack-section" data-testid="stack-section">
      <div className="stack-inner">
        <h2 className="neon-text-green" data-testid="stack-heading">THE MULTI-SERVICE ADVANTAGE</h2>
        <p className="stack-lead" data-testid="stack-lead">Most clients stack 2–4 services.</p>
        <p className="stack-text" data-testid="stack-text">One trusted team. One schedule. Consistent elite quality across the entire property.</p>
        <button
          className="btn btn-secondary stack-cta"
          data-testid="stack-bundle-button"
          onClick={() => {
            navigate("/book");
            setTimeout(() => window.dispatchEvent(new Event("epic:bundle")), 400);
          }}
        >
          Build My Bundle
        </button>
      </div>
    </section>
  );
};

const WhyUs = () => (
  <section id="why-us" className="why-section" data-testid="why-section">
    <h2 className="neon-text-pink" data-testid="why-heading">WHY THE EPIC SQUAD</h2>
    <div className="grid why-grid">
      {WHY_US.map((w, i) => (
        <div className="card why-card" key={i} data-testid={`why-card-${i}`}>
          <w.icon size={40} className="why-icon" strokeWidth={1.5} />
          <h3>{w.title}</h3>
          <p>{w.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const Owner = () => (
  <section className="owner-section" data-testid="owner-section">
    <div className="owner-inner">
      <div className="owner-photo-wrap">
        <img src="/assets/owner.webp" alt="Tristian Turmon — Owner of Epic Expectations" className="owner-photo" data-testid="owner-photo" loading="lazy" />
      </div>
      <div className="owner-copy">
        <p className="owner-rank neon-text-green" data-testid="owner-rank">MEET YOUR COMMANDING OFFICER</p>
        <h2 className="neon-text-pink" data-testid="owner-name">TRISTIAN TURMON</h2>
        <p className="owner-text" data-testid="owner-text">
          Owner-run means owner-accountable. Tristian personally stands behind every job the Epic Squad takes on — no call centers, no runaround, no excuses. You get the man in charge, a fast free quote, and results that earn the name.
        </p>
        <div className="owner-actions">
          <a href={PHONE_TEL} className="btn btn-primary" data-testid="owner-call-button"><Phone size={18} /> Call Tristian</a>
          <Link to="/book" className="btn btn-secondary" data-testid="owner-quote-button">Request a Free Quote</Link>
        </div>
      </div>
    </div>
  </section>
);

const Reviews = () => (
  <section id="reviews" className="reviews-section" data-testid="reviews-section">
    <h2 className="neon-text-green" data-testid="reviews-heading">MISSION REPORTS</h2>
    <p className="section-tagline neon-text-pink" data-testid="reviews-tagline">Real Jobs. Real Results. Five Stars Across the Board.</p>
    <div className="grid reviews-grid">
      {REVIEWS.map((r, i) => (
        <div className="card review-card" key={i} data-testid={`review-card-${i}`}>
          <a href={r.img} target="_blank" rel="noopener noreferrer" title="View full image">
            <img src={r.img} alt={`Before and after — ${r.service}`} className="review-img" loading="lazy" />
          </a>
          <div className="review-stars" data-testid={`review-stars-${i}`}>
            {[...Array(5)].map((_, s) => (
              <Star key={s} size={18} fill="#ffd700" color="#ffd700" />
            ))}
          </div>
          <p className="review-text">&ldquo;{r.text}&rdquo;</p>
          <p className="review-author">&mdash; {r.name}</p>
          <p className="review-service">{r.service}</p>
        </div>
      ))}
    </div>
  </section>
);

const SquadPhotos = () => (
  <section className="squad-section" data-testid="squad-section">
    <h2 className="neon-text-blue" data-testid="squad-heading">THE EPIC SQUAD IN ACTION</h2>
    <div className="squad-grid">
      {SQUAD_PHOTOS.map((p, i) => (
        <figure className="squad-item" key={i} data-testid={`squad-photo-${i}`}>
          <a href={p.src} target="_blank" rel="noopener noreferrer" title="View full image">
            <img src={p.src} alt={p.caption} loading="lazy" />
          </a>
          <figcaption>{p.caption}</figcaption>
        </figure>
      ))}
    </div>
  </section>
);

const Showcase = () => (
  <section className="showcase-section" data-testid="showcase-section">
    <div className="showcase-inner">
      <img src="/assets/billboard-command.webp" alt="Epic Expectations billboard — Request a Quote Today" className="showcase-img" data-testid="showcase-image" loading="lazy" />
      <div className="showcase-copy">
        <h2 className="neon-text-blue" data-testid="showcase-heading">REQUEST A QUOTE TODAY!</h2>
        <p data-testid="showcase-text">Call, text or book online — the Epic Squad answers fast and quotes free.</p>
        <div className="showcase-buttons">
          <a href={PHONE_TEL} className="btn btn-primary" data-testid="showcase-call-button"><Phone size={18} /> Call Now</a>
          <a href={PHONE_SMS} className="btn btn-secondary" data-testid="showcase-text-button"><MessageCircle size={18} /> Text Us</a>
        </div>
      </div>
    </div>
  </section>
);

const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", preferred_date: "", details: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onBundle = () => setForm((f) => ({ ...f, service: "multiple" }));
    window.addEventListener("epic:bundle", onBundle);
    return () => window.removeEventListener("epic:bundle", onBundle);
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Request received! The Epic Squad will reach out shortly.");
      setForm({ name: "", phone: "", email: "", service: "", preferred_date: "", details: "" });
    } catch (err) {
      toast.error("Could not send your request. Please call or text us at " + PHONE_DISPLAY);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="book" className="form-section" data-testid="booking-section">
      <div className="form-container" data-testid="quote-form-container">
        <h2 className="neon-text-pink" data-testid="quote-form-heading">REQUEST A QUOTE &amp; BOOK</h2>
        <form onSubmit={onSubmit} data-testid="quote-form">
          <input type="text" name="name" placeholder="Full Name" required value={form.name} onChange={onChange} data-testid="quote-name-input" />
          <input type="tel" name="phone" placeholder="Phone Number" required value={form.phone} onChange={onChange} data-testid="quote-phone-input" />
          <input type="email" name="email" placeholder="Email Address" required value={form.email} onChange={onChange} data-testid="quote-email-input" />
          <select name="service" required value={form.service} onChange={onChange} data-testid="quote-service-select">
            <option value="" disabled>Select an Elite Service...</option>
            {SERVICES.map((s) => (
              <option key={s.value} value={s.value}>{s.name}</option>
            ))}
            <option value="multiple">Multiple Services (List in Details)</option>
          </select>
          <div className="date-field">
            <CalendarDays size={18} className="date-icon" />
            <input type="date" name="preferred_date" value={form.preferred_date} onChange={onChange} data-testid="quote-date-input" aria-label="Preferred service date" />
          </div>
          <textarea name="details" rows="4" placeholder="Property Details or Specific Requests..." value={form.details} onChange={onChange} data-testid="quote-details-input"></textarea>
          <button type="submit" className="submit-btn" disabled={sending} data-testid="quote-submit-button">
            {sending ? "SENDING..." : "SEND REQUEST"}
          </button>
        </form>
        <p className="form-alt" data-testid="quote-alt-contact">
          Prefer to talk? <a href={PHONE_TEL} className="neon-text-green" data-testid="quote-call-link">Call {PHONE_DISPLAY}</a> or <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="neon-text-green" data-testid="quote-whatsapp-link">WhatsApp us</a>
        </p>
      </div>
    </section>
  );
};

const ContactBlock = () => (
  <section className="contact-page-section" data-testid="contact-page-section">
    <div className="form-container contact-card" data-testid="contact-card">
      <img src="/assets/logo.webp" alt="Epic Expectations logo" className="footer-logo" />
      <h2 className="neon-text-blue" data-testid="contact-page-heading">CONTACT COMMAND</h2>
      <p className="footer-slogan neon-text-pink">Victory Over Every Task&trade;</p>
      <div className="contact-info">
        <p data-testid="contact-page-owner"><strong>Commanding Officer:</strong> Tristian Turmon</p>
        <p data-testid="contact-page-phone">
          <strong>Comms Line:</strong>{" "}
          <a href={PHONE_TEL} className="neon-text-green" data-testid="contact-page-phone-link">{PHONE_DISPLAY}</a>
        </p>
        <p data-testid="contact-page-email">
          <strong>Dispatch:</strong>{" "}
          <a href={`mailto:${EMAIL}`} className="neon-text-pink" data-testid="contact-page-email-link">{EMAIL}</a>
        </p>
        <p data-testid="contact-page-email-direct">
          <strong>Direct:</strong>{" "}
          <a href={`mailto:${EMAIL_GMAIL}`} className="neon-text-pink" data-testid="contact-page-email-direct-link">{EMAIL_GMAIL}</a>
        </p>
        <div className="footer-actions">
          <a href={PHONE_TEL} className="footer-action" data-testid="contact-page-call-link"><Phone size={16} /> Call</a>
          <a href={PHONE_SMS} className="footer-action" data-testid="contact-page-sms-link"><MessageCircle size={16} /> Text</a>
          <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="footer-action" data-testid="contact-page-whatsapp-link"><MessageCircle size={16} /> WhatsApp</a>
          <a href={`mailto:${EMAIL}`} className="footer-action" data-testid="contact-page-mail-link"><Mail size={16} /> Email</a>
        </div>
      </div>
      <p className="form-alt" style={{ marginTop: 26 }}>
        Ready to deploy the squad? <Link to="/book" className="neon-text-green" data-testid="contact-page-book-link">Book online now</Link>
      </p>
    </div>
  </section>
);

const Careers = () => {
  const [positions, setPositions] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/careers/positions`).then(({ data }) => setPositions(data.positions)).catch(() => {});
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/careers/apply`, form);
      toast.success("Application received! The Commanding Officer reviews every recruit personally.");
      setForm({ name: "", email: "", phone: "", position: "", message: "" });
    } catch (err) {
      toast.error("Could not send your application. Please call or text us at " + PHONE_DISPLAY);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="careers-section" data-testid="careers-section">
      <div className="careers-hero">
        <p className="hero-slogan neon-text-green">Now Recruiting</p>
        <h2 className="neon-text-pink" data-testid="careers-heading">JOIN THE EPIC SQUAD</h2>
        <p className="careers-lead" data-testid="careers-lead">
          We don&apos;t do average — we deliver epic. Train across all 7 elite services, work with an owner who stands behind every job, and build a career with real momentum.
        </p>
        <div className="careers-perks">
          <span>Flexible Scheduling</span>
          <span>Weekly Payroll</span>
          <span>Multi-Trade Training</span>
          <span>Growth Into Squad Leader Roles</span>
        </div>
      </div>
      <div className="form-container" data-testid="careers-form-container">
        <h2 className="neon-text-blue" data-testid="careers-form-heading">APPLY TO THE SQUAD</h2>
        <form onSubmit={onSubmit} data-testid="careers-form">
          <input type="text" name="name" placeholder="Full Name" required value={form.name} onChange={onChange} data-testid="careers-name-input" />
          <input type="email" name="email" placeholder="Email Address" required value={form.email} onChange={onChange} data-testid="careers-email-input" />
          <input type="tel" name="phone" placeholder="Phone Number" required value={form.phone} onChange={onChange} data-testid="careers-phone-input" />
          <select name="position" required value={form.position} onChange={onChange} data-testid="careers-position-select">
            <option value="" disabled>Choose Your Mission...</option>
            {positions.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <textarea name="message" rows="4" placeholder="Experience, availability, or anything we should know..." value={form.message} onChange={onChange} data-testid="careers-message-input"></textarea>
          <button type="submit" className="submit-btn" disabled={sending} data-testid="careers-submit-button">
            {sending ? "SENDING..." : "SUBMIT APPLICATION"}
          </button>
        </form>
      </div>
    </section>
  );
};

const TERMS_SECTIONS = [
  {
    title: "THE EPIC GUARANTEE",
    text: "Every job is held to battle-ready standards. If any service falls short, contact us within 48 hours of completion and the Epic Squad will return and make it right at no additional charge. We don't do average — we deliver epic, and we stand behind every mission.",
  },
  {
    title: "RESCHEDULING & WEATHER POLICY",
    text: "Outdoor missions — landscaping, pressure washing, window & gutter cleaning, and mobile detailing — are weather permitting. If rain, storms, or unsafe conditions move in, we contact you as early as possible and rebook you at the next available slot, always free of charge. Life happens inside too: reschedule or cancel any appointment free of charge up to 24 hours before your scheduled time.",
  },
  {
    title: "QUOTES & ESTIMATES",
    text: "Every quote is free and confirmed before work begins. Online price ranges are honest estimates for standard jobs — your official quote locks in the price for the scope listed. If the scope of work changes significantly upon arrival, we discuss it with you before executing. No surprises, ever.",
  },
  {
    title: "PAYMENT POLICY",
    text: "Payment is due upon completion of services unless otherwise agreed in writing. We accept cash, check, credit/debit card, and digital payment (Zelle / Venmo). Every completed job comes with an official receipt/invoice for your records.",
  },
  {
    title: "PROPERTY ACCESS & PREPARATION",
    text: "Some missions need access to water and/or power (pressure washing, detailing, carpet extraction). Please secure pets and clear the work area of personal items before the squad arrives. We treat your property like our own headquarters — with discipline and respect.",
  },
  {
    title: "MULTI-SERVICE ADVANTAGE",
    text: "Bundle 2 or more services on one visit and one schedule. Ask about on-site bundle pricing when the squad is already deployed at your property.",
  },
];

const Terms = () => (
  <section className="terms-section" data-testid="terms-section">
    <h2 className="neon-text-blue" data-testid="terms-heading">TERMS &amp; POLICIES</h2>
    <p className="section-tagline neon-text-green" data-testid="terms-tagline">Clear Orders. No Fine-Print Ambush.</p>
    <div className="terms-list">
      {TERMS_SECTIONS.map((t, i) => (
        <div className="card terms-card" key={i} data-testid={`terms-card-${i}`}>
          <h3>{t.title}</h3>
          <p>{t.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const Footer = () => (
  <footer id="contact" data-testid="site-footer">
    <img src="/assets/logo.webp" alt="Epic Expectations logo" className="footer-logo" data-testid="footer-logo" />
    <div className="logo neon-text-blue" style={{ marginBottom: 20 }}>EPIC EXPECTATIONS&trade;</div>
    <p className="footer-slogan neon-text-pink" data-testid="footer-slogan">Victory Over Every Task&trade;</p>
    <nav className="footer-nav" data-testid="footer-nav">
      {NAV_LINKS.map((l) => (
        <Link key={l.to} to={l.to} data-testid={`footer-${l.testid}`}>{l.label}</Link>
      ))}
      <Link to="/terms" data-testid="footer-terms-link">Terms &amp; Policies</Link>
      <Link to="/admin" className="footer-login" data-testid="footer-team-login-link">Team Login</Link>
    </nav>
    <div className="contact-info" data-testid="contact-info">
      <p data-testid="contact-owner"><strong>Commanding Officer:</strong> Tristian Turmon</p>
      <p data-testid="contact-phone">
        <strong>Comms Line:</strong>{" "}
        <a href={PHONE_TEL} className="neon-text-green" data-testid="contact-phone-link">{PHONE_DISPLAY}</a>
      </p>
      <p data-testid="contact-email">
        <strong>Dispatch:</strong>{" "}
        <a href={`mailto:${EMAIL}`} className="neon-text-pink" data-testid="contact-email-link">{EMAIL}</a>
      </p>
      <p data-testid="contact-email-direct">
        <strong>Direct:</strong>{" "}
        <a href={`mailto:${EMAIL_GMAIL}`} className="neon-text-pink" data-testid="contact-email-direct-link">{EMAIL_GMAIL}</a>
      </p>
    </div>
    <p style={{ marginTop: 30, fontSize: "0.9rem", color: "#666" }} data-testid="footer-copyright">
      &copy; 2026 Epic Expectations&trade; Home &amp; Lawn Services. All Rights Reserved.
    </p>
  </footer>
);

const Page = ({ children }) => (
  <div className="epic-app" data-testid="app-root">
    <Header />
    {children}
    <Footer />
  </div>
);

const HomePage = () => (
  <Page>
    <Hero />
    <StackAdvantage />
    <WhyUs />
    <Owner />
    <Showcase />
  </Page>
);

const ServicesPage = () => (
  <Page>
    <Services />
    <StackAdvantage />
  </Page>
);

const ReviewsPage = () => (
  <Page>
    <Reviews />
    <Showcase />
  </Page>
);

const SquadPage = () => (
  <Page>
    <SquadPhotos />
    <Owner />
  </Page>
);

const BookPage = () => (
  <Page>
    <QuoteForm />
    <Showcase />
  </Page>
);

const ContactPage = () => (
  <Page>
    <ContactBlock />
  </Page>
);

const CareersPage = () => (
  <Page>
    <Careers />
    <Showcase />
  </Page>
);

const TermsPage = () => (
  <Page>
    <Terms />
  </Page>
);

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-center" theme="dark" />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/squad" element={<SquadPage />} />
        <Route path="/book" element={<BookPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/pay/:ref" element={<PayPage />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Routes>
    </BrowserRouter>
  );
}
